import { useEffect, useState, type FormEvent } from 'react';
import { REGLAGES_PAR_DEFAUT } from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useAjouterColis } from '../api/hooks';
import type { CommandeListee, DocumentReglages } from '../api/types';
import { ChampsTracking } from './SaisieExpedition';
import { useToasts } from './Toasts';

/**
 * Un colis de plus, quand la commande part en plusieurs envois.
 *
 * Le premier colis reste le tracking de la commande : l'étape, le relevé et la
 * livraison estimée le suivent, lui seul. Celui-ci s'ajoute à côté, avec son
 * numéro et son transporteur, et son mail au client s'ouvre à relire juste
 * après — rien ne part sans avoir été vu.
 */
export function AjoutColis({
  commande, reglages, onFermer, onPreparerMail,
}: {
  commande: CommandeListee;
  reglages: DocumentReglages | undefined;
  onFermer: () => void;
  /** Ouvre le mail du colis à relire, composé sur la commande enregistrée. */
  onPreparerMail: (commande: CommandeListee, tracking: string) => void;
}) {
  const { ajouter } = useToasts();
  const ajout = useAjouterColis();
  const [valeur, setValeur] = useState('');
  const [transporteur, setTransporteur] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  const transporteurs = Object.keys(reglages?.transit ?? REGLAGES_PAR_DEFAUT.transit);
  const tracking = valeur.trim();

  async function enregistrer(sansMail = false): Promise<CommandeListee | null> {
    setErreur(null);
    if (!transporteur) {
      setErreur('Choisissez un transporteur : il donne le lien de suivi du colis.');
      return null;
    }
    try {
      return await ajout.mutateAsync({ id: commande.id, tracking, transporteur, sansMail });
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'L’ajout du colis a échoué.');
      return null;
    }
  }

  async function ajouterSeulement(e: FormEvent) {
    e.preventDefault();
    // Sans mail : le mail du colis est écarté, aucune tâche ne le réclamera.
    if (!(await enregistrer(true))) return;
    ajouter(`Colis ${tracking} ajouté à ${commande.ref}, sans mail au client.`);
    onFermer();
  }

  async function ajouterEtPreparer() {
    const r = await enregistrer();
    if (r) onPreparerMail(r, tracking);
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <form
        className="box" role="dialog" aria-modal="true" aria-label={`Autre colis — ${commande.ref}`}
        style={{ width: 520 }} onSubmit={(e) => void ajouterSeulement(e)}
      >
        <div className="mhead">
          <h2>Autre colis — {commande.ref}</h2>
          <button type="button" className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <p className="hint" style={{ marginTop: 0 }}>
            Premier colis : <span className="mono">{commande.tracking}</span>
            {commande.transporteur && ` · ${commande.transporteur}`}. Il reste celui que suit la
            commande ; ce colis-ci a son propre mail au client.
          </p>

          <ChampsTracking
            prefixe="ac" valeur={valeur} onValeur={setValeur}
            transporteur={transporteur} onTransporteur={setTransporteur}
            transporteurs={transporteurs}
          />

          {erreur && <p className="err" role="alert">{erreur}</p>}
        </div>

        <div className="mfoot">
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button type="submit" className="btn" disabled={!tracking || ajout.isPending}>
            Ajouter sans mail
          </button>
          <button
            type="button" className="btn pri" disabled={!tracking || ajout.isPending}
            onClick={() => void ajouterEtPreparer()}
          >
            {ajout.isPending ? <><span className="spin" /> Enregistrement…</> : 'Ajouter et envoyer'}
          </button>
        </div>
      </form>
    </div>
  );
}
